import { LoadingButton } from "@mui/lab";
import {
  Button,
  Checkbox,
  Grid,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { GridColDef } from "@mui/x-data-grid";
import { FormTextField } from "components/atoms";
import { AppTable, CommonModal } from "components/molecules";
import { FormikProvider, useFormik } from "formik";
import { TableHelper } from "helpers";
import { usePaginationRequest } from "hooks";
import {
  IGetMaterialRequirementSuggestPalletsRequest,
  IMaterialRequirementCreateSuggestPalletRequest,
  IMaterialRequirementDetail,
} from "model";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { MaterialRequirementActions } from "redux/actions";
import { useAppSelector } from "redux/configurations/reduxHooks";

import { MaterialRequirementApiService } from "services";
import { CURRENT_PAGE_SIZE, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";

interface IProductListProps {
  id: string;
}
const SuggestPalletList = (props: IProductListProps) => {
  const { id } = props;
  const dispatch = useDispatch();

  const [refresh, setRefresh] = useState(false);
  const [open, setOpen] = useState(false);
  const action = useAppSelector(
    (state) => state.MaterialRequirementReducer.action
  );
  const [currentPallet, setCurrentPallet] = useState<any>();
  const [request, setRequest] =
    useState<IGetMaterialRequirementSuggestPalletsRequest>({
      requestMaterialId: id,
      pageNumber: FIRST_PAGE,
      pageSize: CURRENT_PAGE_SIZE,
    });

  const { loading, result } = usePaginationRequest<
    IGetMaterialRequirementSuggestPalletsRequest,
    IMaterialRequirementDetail[]
  >(
    request,
    MaterialRequirementApiService.getMaterialRequirementSuggestPallets,
    [],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );

  const _handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber,
    });
  };

  const _handleSearch = (searchText: string) => {
    setRequest({
      ...request,
      searchText,
    });
  };

  const _handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const handleCheckBoxPress = (row: any) => {
    setCurrentPallet(row);
    setOpen(true);
  };

  const formik = useFormik<any>({
    initialValues: {
      bookQty: currentPallet?.bookedQty,
      note: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      const request: IMaterialRequirementCreateSuggestPalletRequest = {
        requestMaterialId: id,
        palletId: currentPallet.palletId,
        productId: currentPallet.productId,
        productLot: currentPallet.productLot,
        bookQty: values.bookQty,
        note: values.note,
      };
      dispatch(MaterialRequirementActions.createBookedPallet.request(request));
    },
  });

  useEffect(() => {
    if (
      action.includes(
        MaterialRequirementActions.createBookedPallet.successName
      ) ||
      action.includes(MaterialRequirementActions.createBookedPallet.successName)
    ) {
      setOpen(false);
      setRefresh(true);
      setCurrentPallet(undefined);
    }
  }, [action]);

  const handleCloseConfirmQuantity = () => {
    formik.resetForm();
    setOpen(false);
    setCurrentPallet(undefined);
  };

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "id",
      headerName: "Book Pallet",
      size: 100,
      renderCell: ({ row }) => (
        <Stack direction={"row"} spacing={1}>
          <Checkbox
            onClick={() => handleCheckBoxPress(row)}
            checked={row.bookedQty > 0}
          />
        </Stack>
      ),
    }),
    TableHelper.createCell({
      field: "strStatus",
      headerName: I18nTranslator(translations.receive.status),
      size: "small",
    }),
    TableHelper.createCell({
      field: "zoneName",
      headerName: I18nTranslator(translations.masterData.zone),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "locationName",
      headerName: I18nTranslator(translations.receive.location),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "palletCode",
      headerName: I18nTranslator(translations.receive.palletCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.receive.productCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productName",
      headerName: I18nTranslator(translations.receive.productName),
      size: "medium",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productLot",
      headerName: I18nTranslator(translations.receive.productLot),
      size: "small",
      align: "left",
    }),

    TableHelper.createCell({
      field: "requestedQty",
      headerName: I18nTranslator(translations.receive.requestQuantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "bookedQty",
      headerName: I18nTranslator(translations.receive.bookedQuantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "quantity",
      headerName: I18nTranslator(translations.receive.palletQuantity),
      size: "small",
      align: "right",
    }),

    TableHelper.createCell({
      field: "strManufacturingDate",
      headerName: I18nTranslator(translations.receive.manufacturingDate),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "strExpiredDate",
      headerName: I18nTranslator(translations.receive.expiredDate),
      size: "small",
      align: "right",
    }),

    TableHelper.createCell({
      field: "note",
      headerName: I18nTranslator(translations.receive.description),
      size: "full",
      align: "right",
    }),
  ];
  return (
    <Fragment>
      <AppTable<any>
        loading={loading}
        data={{
          ...result,
          // data: orderBy(result.data, ["bookedQty", "strExpiredDate"], "desc"),
        }}
        columns={COLUMNS}
        hideAddNew
        onSearch={_handleSearch}
        searchPlaceholder={I18nTranslator(
          translations.placeholder.searchLoadingOrderPallet
        )}
        onChangePage={_handleChangePage}
        removeActionColumn
        hideHeader
        defaultHeaderTable={330}
        onPageSizeChange={_handlePageSizeChange}
      />
      <CommonModal
        title={"Booking pallet"}
        open={open}
        handleClose={handleCloseConfirmQuantity}
        FooterComponent={
          <Footer
            disableConfirm={false}
            onConfirm={formik.submitForm}
            onClose={handleCloseConfirmQuantity}
          />
        }
      >
        <FormikProvider value={formik}>
          <Grid container item xs={12} rowSpacing={2}>
            <Grid container item xs={12}>
              <Box
                sx={{
                  flex: 1,
                  flexDirection: "row",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography>
                  {I18nTranslator(translations.materialRequisition.productCode)}
                </Typography>
                <Typography fontWeight={"bold"}>
                  {currentPallet?.productCode}
                </Typography>
              </Box>
            </Grid>
            <Grid container item xs={12}>
              <Box
                sx={{
                  flex: 1,
                  flexDirection: "row",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography>
                  {I18nTranslator(translations.materialRequisition.productName)}
                </Typography>
                <Typography fontWeight={"bold"}>
                  {currentPallet?.productName}
                </Typography>
              </Box>
            </Grid>
            <Grid container item xs={12}>
              <Box
                sx={{
                  flex: 1,
                  flexDirection: "row",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography>
                  {I18nTranslator(
                    translations.materialRequisition.requestQuantity
                  )}
                </Typography>
                <Typography fontWeight={"bold"}>
                  {currentPallet?.requestedQty}
                </Typography>
              </Box>
            </Grid>
            <Grid container item xs={12}>
              <Box
                sx={{
                  flex: 1,
                  flexDirection: "row",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography>
                  {I18nTranslator(
                    translations.materialRequisition.bookingQuantity
                  )}
                </Typography>
                <Typography fontWeight={"bold"}>
                  {currentPallet?.bookedQty}
                </Typography>
              </Box>
            </Grid>
            <Grid container item xs={12}>
              <Box
                sx={{
                  flex: 1,
                  flexDirection: "row",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography>
                  {I18nTranslator(
                    translations.materialRequisition.productQuantity
                  )}
                </Typography>
                <Typography fontWeight={"bold"}>
                  {currentPallet?.quantity}
                </Typography>
              </Box>
            </Grid>
            <Grid container item xs={12}>
              <FormTextField
                id={"bookQty"}
                label={I18nTranslator(
                  translations.materialRequisition.quantity
                )}
                type={"number"}
                inputMode="decimal"
                InputProps={{
                  inputProps: {
                    inputMode: "decimal",
                    pattern: "[0-9]*",
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      {currentPallet?.uom.toUpperCase()}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid container item xs={12}>
              <FormTextField
                id={"note"}
                label={I18nTranslator(translations.materialRequisition.note)}
              />
            </Grid>
          </Grid>
        </FormikProvider>
      </CommonModal>
    </Fragment>
  );
};

const Footer = (props: {
  disableConfirm: boolean;
  onConfirm(): void;
  onClose(): void;
}) => {
  const { disableConfirm, onConfirm, onClose } = props;
  return (
    <Stack direction={"row"} spacing={2} justifyContent={"flex-end"}>
      <Button onClick={onClose}>
        {I18nTranslator(translations.button.cancel)}
      </Button>
      <LoadingButton
        loading={false}
        disabled={disableConfirm}
        onClick={onConfirm}
        variant="contained"
      >
        {I18nTranslator(translations.button.confirm)}
      </LoadingButton>
    </Stack>
  );
};

export default SuggestPalletList;
